import { Spinner } from "@/components/ui/shadcn-io/spinner";
import Env from "@/config/environment";
import { OAUTH_MESSAGE_SOURCE } from "@/features/external-accounts/oauth-message";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/features/utils/i18n";

/**
 * Where the provider's consent screen comes back to, when its redirect URI
 * points at the back-office rather than at the API.
 *
 * Nothing is connected here, and nothing can be: the code Google hands back is
 * only worth something exchanged against the client secret, which lives in the
 * API. So this page's whole job is to carry the code one hop further, to the
 * API's own callback — the page that creates the account and tells the opener
 * how it went.
 *
 * It used to do neither: it read a `success` parameter that only the *old*
 * backend (which exchanged the code itself and redirected here) ever set, so a
 * consent that landed here announced "Account connected", closed the window and
 * dropped the code. No account was created, no error was shown, and the setting
 * that asked for the connection stayed empty — which is exactly what it looks
 * like when nothing happens at all.
 */

/** The provider is carried by the `state` the API built when the flow started. */
const providerFromState = (state: string | null): string => {
  if (!state) return "google";
  try {
    const decoded = JSON.parse(
      atob(state.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof decoded?.provider === "string" ? decoded.provider : "google";
  } catch {
    return "google";
  }
};

const notifyOpener = (payload: { success: boolean; error?: string }) => {
  try {
    window.opener?.postMessage(
      { source: OAUTH_MESSAGE_SOURCE, ...payload },
      "*",
    );
  } catch {
    // An opener we cannot reach is not a reason to lose the connection: the
    // page that opened us also watches for the window closing.
  }
};

export const OAuthCallbackPage = () => {
  const { t } = useTranslation("externalAccounts");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<{ done: boolean; error?: string }>({
    done: false,
  });

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");
    // "access_denied" fits every refusal; the description is the half that says
    // which one, and it is the only thing the opener can show the user.
    const errorDescription = searchParams.get("error_description");
    // Set by the previous generation of this flow, where the API redirected
    // here once it had already created the account.
    const legacySuccess = searchParams.get("success");

    if (error) {
      const reason = errorDescription
        ? `${decodeURIComponent(error)}: ${decodeURIComponent(errorDescription)}`
        : decodeURIComponent(error);
      setStatus({ done: true, error: reason });
      notifyOpener({ success: false, error: reason });
      return;
    }

    if (code && state) {
      const provider = providerFromState(state);
      const api = Env.server.replace(/\/$/, "");
      window.location.replace(
        `${api}/api/accounts/v1/oauth/callback/${encodeURIComponent(provider)}` +
          `?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
      );
      return;
    }

    if (legacySuccess) {
      notifyOpener({ success: true });
      if (window.opener) {
        window.setTimeout(() => window.close(), 300);
        return;
      }
      navigate("/start");
      return;
    }

    setStatus({ done: true, error: "missing_code" });
    notifyOpener({ success: false, error: "missing_code" });
  }, [searchParams, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center max-w-md p-6">
        {!status.done && (
          <Spinner
            variant="circle"
            className="mx-auto mb-4 size-8 text-muted-foreground"
          />
        )}
        <p className="mt-4 text-sm text-muted-foreground">
          {status.error
            ? t("oauthCallback.failed", { error: status.error })
            : t("oauthCallback.finishing")}
        </p>
      </div>
    </div>
  );
};
